
import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { restaurants } from '../../../mocks/foodDrink';

const featured = restaurants.filter(r => r.rating >= 4.6).slice(0, 8);

export default function FeaturedRestaurants() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    const cardWidth = 340;
    const gap = 24;
    const idx = Math.round(el.scrollLeft / (cardWidth + gap));
    setActiveIndex(idx);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 364;
    el.scrollBy({ left: direction === 'left' ? -amount * 2 : amount * 2, behavior: 'smooth' });
  };

  const totalDots = Math.max(1, featured.length - 2);

  return (
    <section className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-50 rounded-full mb-5">
              <i className="ri-restaurant-line text-rose-500" />
              <span className="text-rose-600 text-sm font-semibold tracking-wide uppercase">
                Top Rated
              </span>
            </div>
            <h4 className="text-4xl md:text-5xl font-bold text-gray-900">
              Featured <span className="text-teal-600">Restaurants</span>
            </h4>
            <p className="text-gray-600 mt-3 max-w-lg">
              Savor the best of Roseville — handpicked dining spots loved by locals and visitors alike
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
              className={`w-12 h-12 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
                canScrollLeft
                  ? 'border-gray-300 text-gray-700 hover:bg-teal-600 hover:text-white hover:border-teal-600 shadow-sm'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll right"
              className={`w-12 h-12 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
                canScrollRight
                  ? 'border-gray-300 text-gray-700 hover:bg-teal-600 hover:text-white hover:border-teal-600 shadow-sm'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Fade edges */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white/80 to-transparent z-10 pointer-events-none" />
          )}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-stone-50/80 to-transparent z-10 pointer-events-none" />
          )}

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {featured.map((restaurant) => (
              <article
                key={restaurant.id}
                className="flex-shrink-0 w-[320px] md:w-[340px] snap-start group"
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 h-full flex flex-col">
                  {/* Image */}
                  <Link to={`/food-drink/${restaurant.id}`} className="cursor-pointer block">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={restaurant.image}
                        alt={`${restaurant.name} - ${restaurant.cuisine} restaurant in Roseville California`}
                        title={`${restaurant.name} Roseville CA`}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                      {/* Cuisine badge */}
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                          <i className="ri-restaurant-2-line text-xs" />
                          {restaurant.cuisine}
                        </span>
                      </div>
                      {/* Rating */}
                      <div className="absolute top-3 right-3">
                        <div className="flex items-center gap-1 px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-full">
                          <i className="ri-star-fill text-amber-400 text-sm" />
                          <span className="text-sm font-bold text-gray-900">{restaurant.rating}</span>
                        </div>
                      </div>
                      {/* Price */}
                      <div className="absolute bottom-3 right-3">
                        <span className="px-2.5 py-1 bg-teal-600 text-white text-xs font-bold rounded-md">
                          {restaurant.priceRange}
                        </span>
                      </div>
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="p-5 flex flex-col flex-1">
                    <Link to={`/food-drink/${restaurant.id}`} className="cursor-pointer">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors mb-1">
                        {restaurant.name}
                      </h3>
                    </Link>
                    <p className="text-xs text-gray-500 font-medium mb-3">{restaurant.category}</p>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-4">
                      {restaurant.description}
                    </p>

                    <div className="mt-auto space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-map-pin-2-line text-teal-500" />
                        </div>
                        <span>{restaurant.address}, Roseville</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-time-line text-teal-500" />
                        </div>
                        <span>{restaurant.hours}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Link
                        to={`/food-drink/${restaurant.id}`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View Details
                        <i className="ri-arrow-right-line text-sm" />
                      </Link>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          restaurant.name + ' ' + restaurant.address + ' Roseville CA'
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                        aria-label={`Get directions to ${restaurant.name}`}
                      >
                        <i className="ri-navigation-line text-base" />
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {Array.from({ length: totalDots }).map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => {
                const el = scrollRef.current;
                if (!el) return;
                el.scrollTo({ left: i * 364, behavior: 'smooth' });
              }}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                activeIndex === i
                  ? 'w-8 h-2.5 bg-teal-500'
                  : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* View All */}
        <div className="text-center mt-10">
          <Link
            to="/food-drink"
            className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restaurant-line" />
            Explore All Restaurants
          </Link>
        </div>
      </div>
    </section>
  );
}
